import { blockYandexGeocodeKey, getYandexMapKeys } from '@noor/api';
import {
  narrowGeocoderResults,
  boundsToSearchWindow,
  TASHKENT_BOUNDS,
  type LegacyGeoResult,
} from '../lib/geocode';

/**
 * Address lookup through the Yandex Geocoder HTTP API (1.x) — both directions:
 * forward takes a street name, reverse takes a `"long, lat"` string, exactly
 * like the `ymaps3.search` call this replaces.
 *
 * It replaces `ymaps3.search` because that SDK call is backed by the Places /
 * Organization Search product (`search-maps.yandex.ru/v1/`), whose key kept
 * answering 403 "Invalid api key" in every environment — while the Geocoder
 * key works and geocoding is all these screens need (see the call sites: a
 * clicked point and a typed street, never an organization lookup).
 *
 * Bare `fetch`, deliberately: this is an external Yandex service with its own
 * key. `@noor/api`'s `http` would inject our gateway's Authorization and
 * x-locale headers and its base URL.
 */

const GEOCODER_URL = 'https://geocode-maps.yandex.ru/1.x/';

export async function geocoderSearch(
  text: string,
  limit: number,
  joiner?: string,
): Promise<LegacyGeoResult[]> {
  // `ll`+`spn`+`rspn=1` restrict results to Tashkent — what `strictBounds`
  // expressed on the SDK call.
  const { ll, spn } = boundsToSearchWindow(TASHKENT_BOUNDS);
  const params = new URLSearchParams({
    geocode: text,
    format: 'json',
    lang: 'ru_RU',
    results: String(limit),
    ll,
    spn,
    rspn: '1',
  });

  const attempted = new Set<string>();
  while (true) {
    const { geocode: apikey } = await getYandexMapKeys();
    if (!apikey || attempted.has(apikey)) {
      throw new Error('No usable Yandex Geocoder API key available');
    }
    attempted.add(apikey);
    params.set('apikey', apikey);
    const response = await fetch(`${GEOCODER_URL}?${params.toString()}`);
    if (response.ok) {
      return narrowGeocoderResults(await response.json(), joiner ?? ', ');
    }
    if (response.status === 403) {
      const body: unknown = await response.json().catch(() => null);
      if (
        body !== null &&
        typeof body === 'object' &&
        'message' in body &&
        typeof body.message === 'string' &&
        body.message.trim().toLowerCase() === 'invalid api key'
      ) {
        blockYandexGeocodeKey(apikey);
        continue;
      }
    }
    throw new Error(`Geocoder answered ${response.status}`);
  }
}
